import { Stats, styled, Text, Tooltip } from '@meterup/atto';

import type { TimeFormatValue, Timestamp } from '../topics/time';
import {
  type TooltipTimezone,
  useTimestampDisplayContext,
} from '../providers/TimestampDisplayContext';
import { formatDateTime, parseDateTime, TimeFormat } from '../topics/time';
import { NoValue } from './NoValue';

export type { TooltipTimezone };

/**
 * Renders multi-timezone tooltip content for a timestamp. Use as Tooltip contents when the
 * trigger is not the timestamp itself (e.g. a Badge).
 */
export function TimestampTooltipContent({
  value,
  tooltipTimezones: tooltipTimezonesProp,
}: {
  value: Timestamp | null | undefined;
  tooltipTimezones?: TooltipTimezone[];
}) {
  const context = useTimestampDisplayContext();
  if (!value) return null;
  const dt = parseDateTime(value);
  if (!dt) return null;

  const tooltipTimezones = tooltipTimezonesProp ?? context?.tooltipTimezones;
  if (tooltipTimezones && tooltipTimezones.length > 0) {
    return (
      <Stats
        direction="column"
        gutter={{ edge: 'none' }}
        size="small"
        stats={tooltipTimezones.map(({ zone, label }) => ({
          label,
          value: (
            <Text>
              {formatDateTime(value, TimeFormat.LONG, zone)}{' '}
              {formatDateTime(value, TimeFormat.U_NAMED_OFFSET, zone)}
            </Text>
          ),
        }))}
      />
    );
  }
  return (
    <Text>
      {formatDateTime(dt, TimeFormat.LONG)} {formatDateTime(dt, TimeFormat.U_NAMED_OFFSET)}
    </Text>
  );
}

export const FormatDateTimeTooltip = styled('div', {
  display: 'flex',
  padding: '$8',
});
FormatDateTimeTooltip.displayName = 'FormatDateTimeTooltip';

const BARE_CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Displays date time with the given format, with a tooltip displaying the locale-full timestamp.
 * Optional displayZone and tooltipTimezones (from props or TimestampDisplayContext) enable
 * showing timestamps in network timezone, UTC, and user local time.
 */
export function FormatDateTime({
  value,
  format,
  dateOnly = false,
  displayZone: displayZoneProp,
  tooltipTimezones: tooltipTimezonesProp,
  size,
}: {
  /** Table cells may pass the generic scalar union; booleans are treated as empty. */
  value: Timestamp | string | number | boolean | null | undefined;
  format?: TimeFormat | TimeFormatValue;
  /**
   * The value is a calendar date (e.g. a Postgres DATE column), not an instant:
   * render the day it names, with no time, zone conversion, or timestamp tooltip.
   * Accepts both the midnight-UTC DateTime wire format and bare YYYY-MM-DD.
   */
  dateOnly?: boolean;
  displayZone?: string;
  tooltipTimezones?: TooltipTimezone[];
  size?: React.ComponentProps<typeof Text>['size'];
}) {
  const context = useTimestampDisplayContext();
  const displayZone = displayZoneProp ?? context?.displayZone;
  const tooltipTimezones = tooltipTimezonesProp ?? context?.tooltipTimezones;

  if (typeof value === 'boolean') return <NoValue />;
  if (!value) return <NoValue />;

  // Luxon parses a bare date at LOCAL midnight, so a calendar date must be
  // pinned to UTC before parsing or the rendered day shifts east of Greenwich.
  const normalized =
    dateOnly && typeof value === 'string' && BARE_CALENDAR_DATE.test(value)
      ? `${value}T00:00:00Z`
      : value;
  const dt = parseDateTime(normalized);
  if (!dt) return <NoValue />;

  if (dateOnly) {
    // A calendar day is zone-free: always read it back in UTC, and skip the
    // timestamp tooltip — there is no finer precision to reveal.
    return <Text size={size}>{formatDateTime(dt, format ?? TimeFormat.SHORT, 'UTC')}</Text>;
  }

  const displayText = formatDateTime(dt, format ?? TimeFormat.MEDIUM, displayZone);

  if (!value) return <NoValue />;

  return (
    <Tooltip
      tight
      contents={
        <FormatDateTimeTooltip>
          <TimestampTooltipContent value={value} tooltipTimezones={tooltipTimezones} />
        </FormatDateTimeTooltip>
      }
    >
      <Text size={size}>{displayText}</Text>
    </Tooltip>
  );
}
